import { compose, lifecycle, withProps } from 'recompose';
import { connect } from 'react-redux';
import delve from 'dlv';
import { string, shape, arrayOf, oneOfType, object, bool } from 'prop-types';
import { IToolbarItemType } from '../GenericComponent';

import { EditButton } from './EditButton';
import { ExportButton } from './ExportButton';
import { GlossaryButton } from './GlossaryButton';

import {
  shouldLoadGlossary,
  loadGlossary,
  glossaryPropType,
} from 'redux/reducers/glossary';

import stringToSlug from 'helpers/stringToSlug';
import userCanEdit from 'helpers/userCanEdit';

import { OptionsWrapper } from './ToolbarOptions.style';

const ListItem = ({ item, title }) => {
  switch (item.type) {
    case IToolbarItemType.Glossary:
      return <GlossaryButton item={item} />;
    case IToolbarItemType.Export:
      return <ExportButton data={item} title={title} />;
    case IToolbarItemType.Edit:
      return <EditButton item={item} />;
    default:
      return null;
  }
};

const ToolbarOptions = (props) => {
  const {
    glossary,
    glossarySlug,
    extraToolbarItems = [],
    userCanEdit,
    title,
  } = props;

  let toolbarItems = [];

  if (glossarySlug) {
    const [categorySlug, termSlug] = glossarySlug.split('/');

    const hasGlossaryTerm =
      glossary.success &&
      Boolean(
        delve(
          glossary.data.find((category) => category.slug === categorySlug),
          'topics',
          []
        ).find((term) => term.slug === termSlug)
      );

    if (hasGlossaryTerm || userCanEdit) {
      toolbarItems.push({
        to: {
          pathname: `/glossary/${stringToSlug(glossarySlug)}`,
          state: {
            modal: { openFromSide: 'right' },
          },
        },
        label: hasGlossaryTerm ? 'Glossary' : 'Add to glossary',
        order: 0,
        type: IToolbarItemType.Glossary,
      });
    }
  }

  const exportItems = extraToolbarItems.filter(
    (item) => item.type === IToolbarItemType.Export
  );
  if (exportItems.length > 0) {
    const exportItem = {
      type: IToolbarItemType.Export,
      items: [...exportItems],
      order: 1,
    };
    toolbarItems = [...toolbarItems, exportItem];
  }
  const editItem = extraToolbarItems.find(
    (item) => item.type === IToolbarItemType.Edit
  );
  if (editItem !== undefined) {
    toolbarItems = [...toolbarItems, editItem];
  }

  return (
    <OptionsWrapper>
      {toolbarItems.map((item) => {
        const cyKey = `toolbar-options-item-${item.order}`;

        return (
          <li key={cyKey} data-pw={cyKey}>
            <ListItem item={item} title={title} />
          </li>
        );
      })}
    </OptionsWrapper>
  );
};

ToolbarOptions.propTypes = {
  /** The glossary slug in the API */
  glossarySlug: string,
  /** Array of configuration objects for the Toolbar options */
  extraToolbarItems: arrayOf(
    shape({
      to: oneOfType([object, string]),
      href: string,
      label: string,
      otherProps: object,
    })
  ),
  /** Optional className, required for styled-components composition */
  className: string,
  /** Can this user edit the glossary */
  userCanEdit: bool,
  /** Glossary reducer state */
  glossary: glossaryPropType.isRequired,
};

export default compose(
  connect(
    (state) => ({
      profile: state.authentication.profile,
      glossary: state.glossary,
    }),
    {
      shouldLoadGlossary,
      loadGlossary,
    }
  ),
  lifecycle({
    componentDidMount() {
      if (this.props.shouldLoadGlossary()) {
        this.props.loadGlossary();
      }
    },
  }),
  withProps(({ profile }) => ({
    userCanEdit: userCanEdit(profile),
  }))
)(ToolbarOptions);
